import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';

import AutoDebitRegistrationDialog from './AutoDebitRegistrationDialog';

import { useAutoDebitRegistrationQuery } from 'apollo/queries/autogenerate/hooks';
import {
  AutoDebitSettlementMethodStatus,
  AutoDebitStatus,
} from 'apollo/queries/autogenerate/schemas';
import { getBadgeDetails } from 'shared/modules/auto-debit/utils/badge-details';
import Badge from 'shared/modules/common/components/atoms/Badge';
import Button from 'shared/modules/common/components/atoms/Button';
import {
  ACTION,
  ITEM,
  SCREEN,
} from 'shared/modules/settlement/constants/mixpanel';
import Mixpanel from 'shared/vendor/Mixpanel';
import DeleteAutoDebitDialog from 'web/screens/Authenticated/Account/PaymentDetails/DeleteAutoDebitDialog';
import ResumeAutoDebitDialog from 'web/screens/Authenticated/Account/PaymentDetails/ResumeAutoDebitDialog';

type Props = {
  autoDebitStatus?: AutoDebitSettlementMethodStatus;
  isBillUpcoming: boolean;
  nextAutoDebitByDate: string;
  pullDate: string;
  isBillOverdue: boolean;
  isBillGenerated: boolean;
  isAccountAvailable: boolean;
  billingCycle: string;
};

export const AutoDebitSection = (props: Props) => {
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const isBigScreen = useMediaQuery({ minWidth: 768 });

  const {
    data: autoDebitRegistration,
    refetch: refetchAutoDebitRegistration,
  } = useAutoDebitRegistrationQuery({
    fetchPolicy: 'cache-and-network',
  });

  const autoDebitRegStatus =
    autoDebitRegistration?.autoDebitRegistration.status;

  const isAutoDebitRegistered =
    autoDebitRegStatus === AutoDebitStatus.ACTIVE ||
    autoDebitRegStatus === AutoDebitStatus.SUSPENDED;

  const autoDebitRegistrationId =
    autoDebitRegistration?.autoDebitRegistration.id;

  const autoDebitAccount = autoDebitRegistration?.autoDebitRegistration.account;
  const adrsResult = useSearchParams()[0].get('adrs') as '0' | '1' | undefined;

  useEffect(() => {
    if (adrsResult) {
      setShowRegistrationDialog(true);
    }
  }, []);

  const displayBadge =
    props.autoDebitStatus === AutoDebitSettlementMethodStatus.APPLIED ||
    props.autoDebitStatus === AutoDebitSettlementMethodStatus.ACTIVE ||
    props.autoDebitStatus === AutoDebitSettlementMethodStatus.PENDING;

  const { title, variant } = getBadgeDetails(props.autoDebitStatus);

  return (
    <View style={styles.section}>
      <View style={styles.titleContainer}>
        <Text style={[styles.sectionHeading, { marginRight: 14 }]}>
          口座振替（自動引落）
        </Text>
        {displayBadge && <Badge title={title} variant={variant} />}
      </View>
      {props.autoDebitStatus === AutoDebitSettlementMethodStatus.APPLIED &&
        props.isBillUpcoming && (
          <Text style={[styles.text, styles.textLight]}>
            {moment().month() + 1}月{moment().endOf('month').format('DD')}
            日までに手続きが完了しなかった場合、お手数ですが、コンビニもしくは銀行振込でお支払いください。
          </Text>
        )}
      <Text style={styles.subHeading}>手数料</Text>
      <Text style={[styles.text, styles.textLight]}>無料</Text>
      <Text style={styles.subHeading}>お引き落とし日</Text>
      <Text style={[styles.text, styles.textLight]}>{props.pullDate}</Text>
      <Text style={[styles.text, styles.textLight]}>
        （{props.nextAutoDebitByDate}までにご利用明細に反映）
      </Text>
      {autoDebitAccount && isAutoDebitRegistered && (
        <>
          <Text style={styles.subHeading}>登録中の銀行口座</Text>
          <Text style={[styles.text, styles.textLight]}>
            {autoDebitAccount.bankName}
          </Text>
          <Text style={styles.subHeading}>口座番号</Text>
          <Text style={[styles.text, styles.textLight]}>
            {autoDebitAccount.number}
          </Text>
          <Text style={styles.subHeading}>口座名義（カタカナ）</Text>
          <Text style={[styles.text, styles.textLight]}>
            {autoDebitAccount.name}
          </Text>
        </>
      )}
      <Text style={styles.subHeading}>支払い方法</Text>
      <Text style={[styles.text, styles.textLight]}>
        お引き落とし日の前営業日までに、ご登録の銀行口座にご入金ください。
      </Text>
      <Text style={[styles.text, styles.textLight]}>
        お引き落としがご利用明細に反映されるのは、金融機関から当社に情報が到着してからとなります。数日お時間がかかりますので、ご了承ください。
      </Text>
      <View style={[styles.footer, isBigScreen && styles.footerBigScreen]}>
        {!props.isAccountAvailable && (
          <Button
            variant="primary"
            title="口座振替に申し込む"
            disabled={props.isBillOverdue}
            style={[
              styles.buttonPrimary,
              isBigScreen && styles.buttonBigScreen,
            ]}
            onPress={() => {
              Mixpanel.trackEvent(
                `${SCREEN.SETTLEMENT_INFO} - ${ACTION.CLICK}`,
                {
                  Item: ITEM.APPLY_FOR_AUTO_DEBIT,
                }
              );

              setShowRegistrationDialog(true);
            }}
          />
        )}

        {isAutoDebitRegistered && (
          <Button
            variant="outlined"
            disabled={props.isBillOverdue}
            title="削除"
            style={[styles.button, isBigScreen && styles.buttonBigScreen]}
            onPress={() => {
              Mixpanel.trackEvent(
                `${SCREEN.SETTLEMENT_INFO} - ${ACTION.CLICK}`,
                {
                  Item: ITEM.UNREGISTER_BANK,
                }
              );

              setShowDeleteDialog(true);
            }}
          />
        )}
        {props.autoDebitStatus === AutoDebitSettlementMethodStatus.INACTIVE &&
          isAutoDebitRegistered && (
            <Button
              variant="primary"
              title="変更する"
              disabled={props.isBillOverdue}
              style={[
                styles.buttonPrimary,
                isBigScreen && styles.buttonBigScreen,
              ]}
              onPress={() => {
                Mixpanel.trackEvent(
                  `${SCREEN.SETTLEMENT_INFO} - ${ACTION.CLICK}`,
                  {
                    Item: ITEM.CHANGE_TO_AUTO_DEBIT,
                  }
                );

                setShowResumeDialog(true);
              }}
            />
          )}
      </View>
      <AutoDebitRegistrationDialog
        open={showRegistrationDialog}
        onClose={() => setShowRegistrationDialog(false)}
        result={adrsResult}
      />
      <DeleteAutoDebitDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onSuccess={() => {
          setShowDeleteDialog(false);
          refetchAutoDebitRegistration();
        }}
        autoDebitId={autoDebitRegistrationId || ''}
        isAutoDebitActive={
          props.autoDebitStatus === AutoDebitSettlementMethodStatus.ACTIVE
        }
        billingCycle={props.billingCycle}
      />
      <ResumeAutoDebitDialog
        open={showResumeDialog}
        onClose={() => setShowResumeDialog(false)}
        onSuccess={() => {
          Mixpanel.trackEvent(
            `${SCREEN.SETTLEMENT_INFO_CHANGE_CONFIRMATION} - ${ACTION.CLICK}`,
            {
              Item: ITEM.CHANGE_TO_AUTO_DEBIT,
            }
          );

          setShowResumeDialog(false);
        }}
        nextAutoDebitByDate={props.nextAutoDebitByDate}
        pullDate={props.pullDate}
        isBillGenerated={props.isBillGenerated}
        billingCycle={props.billingCycle}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  titleContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 16,
  },
  button: {
    marginHorizontal: 10,
    marginBottom: 16,
  },
  buttonBigScreen: {
    alignSelf: 'center',
  },
  buttonPrimary: {
    marginHorizontal: 10,
    marginBottom: 16,
    alignSelf: 'center',
  },
  section: {
    backgroundColor: 'white',
    padding: 24,
    borderRadius: 24,
    marginBottom: 24,
  },
  sectionHeading: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 'bold',
    color: '#2A2E32',
  },
  text: {
    fontSize: 13,
    lineHeight: 16,
    color: '#2A2E32',
    marginBottom: 16,
  },
  textLight: {
    color: '#586065',
  },
  footer: {
    marginBottom: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerBigScreen: {
    flexDirection: 'row',
  },
  subHeading: {
    fontSize: 13,
    lineHeight: 16,
    fontWeight: '600',
    color: '#586065',
    marginBottom: 8,
  },
});
